import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Box, Chip, FormControlLabel, Stack, Switch } from '@mui/material'
import { PageTitle } from '../components/atoms/PageTitle'
import { Text } from '../components/atoms/Text'
import { Button } from '../components/atoms/Button'
import { callAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'

interface BrpRow {
  code: string
  codingScheme: string | null
  name: string | null
  businessId: string | null
  country: string | null
  email: string | null
  emailSource: string | null
  notes: string | null
  retailerCount: number
  customerCount: number
}

interface BrpEdit {
  email: string
  emailSource: string
  notes: string
}

const toEdit = (brp: BrpRow): BrpEdit => ({
  email: brp.email || '',
  emailSource: brp.emailSource || '',
  notes: brp.notes || ''
})

export function AdminBrpsPage(): React.ReactElement {

  const { t } = useTranslation()
  const app = useApp()
  const [brps, setBrps] = useState<BrpRow[]>([])
  const [edits, setEdits] = useState<Record<string, BrpEdit>>({})
  const [filter, setFilter] = useState('')
  const [showAll, setShowAll] = useState(false)
  const [saving, setSaving] = useState<string | null>(null)

  const fetchBrps = () => {
    callAxios<BrpRow[]>({ url: '/admin/brps' }).then(res => {
      if (res.success) {
        setBrps(res.data)
        setEdits(Object.fromEntries(res.data.map(b => [b.code, toEdit(b)])))
      } else {
        app.addError(res)
      }
    })
  }

  useEffect(() => {
    fetchBrps()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setEdit = (code: string, field: keyof BrpEdit, value: string) => {
    setEdits(prev => ({ ...prev, [code]: { ...prev[code], [field]: value } }))
  }

  const isDirty = (brp: BrpRow) => {
    const edit = edits[brp.code]
    return !!edit && (edit.email !== (brp.email || '') ||
      edit.emailSource !== (brp.emailSource || '') ||
      edit.notes !== (brp.notes || ''))
  }

  const onSave = (brp: BrpRow) => {
    const edit = edits[brp.code]
    setSaving(brp.code)
    callAxios({
      method: 'PATCH',
      url: `/admin/brps/${brp.code}`,
    }, { email: edit.email, email_source: edit.emailSource, notes: edit.notes }).then(res => {
      setSaving(null)
      if (res.success) {
        app.addInfo(t('BRP contact saved.'))
        fetchBrps()
      } else {
        app.addError(res)
      }
    })
  }

  const needle = filter.toLowerCase()
  const matches = brps.filter(b =>
    !needle ||
    (b.name || '').toLowerCase().includes(needle) ||
    b.code.toLowerCase().includes(needle) ||
    (b.email || '').toLowerCase().includes(needle))
  // A text search looks through everything; the unfiltered listing hides
  // BRPs we have no customers under (nothing to report to them).
  const visible = needle || showAll ? matches : matches.filter(b => b.customerCount > 0)
  const hidden = matches.length - visible.length

  return (
    <Box>
      <PageTitle title={t('Balance responsible parties')}/>
      <Box sx={{ mb: 2 }}>
        <Trans>Curated notification addresses for reserve-use notifications. The directory itself syncs nightly from eSett open data; the email, its source and the notes survive the sync.</Trans>
      </Box>

      <Stack direction="row" spacing={3} alignItems="center" sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Text
          label={t('Filter by name, code or email')}
          value={filter}
          onChange={e => setFilter(e.target.value)}
          sx={{ minWidth: '20rem' }}
        />
        <FormControlLabel
          control={
            <Switch
              data-cy='switch-Show all BRPs'
              checked={showAll}
              onChange={e => setShowAll(e.target.checked)}
            />}
          label={t('Show all') + (hidden > 0 ? ` (+${hidden})` : '')}
        />
      </Stack>

      {visible.map(brp =>
        <Box
          key={brp.code}
          borderRadius={2}
          padding={1.5}
          sx={{ border: '1px solid rgba(128,128,128,0.35)', mb: 1 }}
          data-cy={`brp-${brp.code}`}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Box sx={{ fontWeight: 600 }}>{brp.name || brp.code}</Box>
            <Box sx={{ fontFamily: 'monospace', opacity: 0.6, fontSize: '0.85em' }}>{brp.code}</Box>
            {brp.customerCount > 0 &&
              <Chip size="small" color="primary" label={`${brp.customerCount} ${t('customers')}`}/>}
            {brp.retailerCount > 0 &&
              <Chip size="small" label={`${brp.retailerCount} ${t('retailers')}`}/>}
            {brp.businessId &&
              <Box sx={{ opacity: 0.5, fontSize: '0.85em' }}>{brp.businessId}</Box>}
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <Text
              label={t('Email')}
              value={edits[brp.code]?.email || ''}
              onChange={e => setEdit(brp.code, 'email', e.target.value)}
              sx={{ flex: 1 }}
            />
            <Text
              label={t('Source')}
              value={edits[brp.code]?.emailSource || ''}
              onChange={e => setEdit(brp.code, 'emailSource', e.target.value)}
              sx={{ flex: 2 }}
            />
            <Button
              data-cy={`button-Save BRP ${brp.code}`}
              onClick={() => onSave(brp)}
              disabled={!isDirty(brp) || saving === brp.code}
            >
              <Trans>Save</Trans>
            </Button>
          </Stack>
          <Text
            label={t('Notes')}
            value={edits[brp.code]?.notes || ''}
            onChange={e => setEdit(brp.code, 'notes', e.target.value)}
            fullWidth
            multiline
            sx={{ mt: 1.5 }}
          />
        </Box>
      )}
    </Box>
  )
}
